import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { queryKeys } from '@/lib/queries';
import type { AuditLogEntry, AuditAction } from '@forest/contracts';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Table, Th, Td } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';

const ACTIONS: AuditAction[] = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'LOGIN',
  'LOGOUT',
  'IMPORT',
  'ATTACH',
  'DETACH',
  'DEACTIVATE',
  'REACTIVATE',
  'ROLE_CHANGE',
  'PASSWORD_RESET',
  'RESTORE',
];

export function AuditPage() {
  const { t } = useTranslation(['admin', 'common']);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<{
    action?: AuditAction;
    entityType?: string;
    dateFrom?: string;
    dateTo?: string;
  }>({});

  const params = { page, limit: 50, ...filters };
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.admin.audit(params),
    queryFn: () =>
      api
        .get('admin/audit', { searchParams: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '')) as Record<string, string | number> })
        .json<{ items: AuditLogEntry[]; pagination: { total: number; totalPages: number } }>(),
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{t('audit.title')}</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('audit.subtitle')}</p>
      </div>
      <Card>
        <CardHeader>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
            <Select
              value={filters.action ?? ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, action: (e.target.value || undefined) as AuditAction | undefined }))
              }
            >
              <option value="">{t('audit.filterAction')}</option>
              {ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Select>
            <Input
              placeholder={t('audit.filterEntity')}
              value={filters.entityType ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, entityType: e.target.value || undefined }))}
            />
            <Input
              type="date"
              value={filters.dateFrom ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value || undefined }))}
            />
            <Input
              type="date"
              value={filters.dateTo ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value || undefined }))}
            />
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading && !data ? (
            <div className="flex justify-center p-10">
              <Spinner />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('audit.col.when')}</Th>
                  <Th>{t('audit.col.who')}</Th>
                  <Th>{t('audit.col.action')}</Th>
                  <Th>{t('audit.col.entity')}</Th>
                  <Th>{t('audit.col.ip')}</Th>
                  <Th>{t('audit.details')}</Th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((row) => (
                  <tr key={row.id}>
                    <Td className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(row.createdAt).toLocaleString()}
                    </Td>
                    <Td>{row.userName ?? '—'}</Td>
                    <Td>
                      <Badge>{row.action}</Badge>
                    </Td>
                    <Td className="font-mono text-xs">
                      {row.entityType}
                      {row.entityId ? ` · ${row.entityId.slice(0, 8)}` : ''}
                    </Td>
                    <Td className="text-xs text-slate-500 dark:text-slate-400">{row.ipAddress ?? '—'}</Td>
                    <Td>
                      <details className="text-xs">
                        <summary className="cursor-pointer text-slate-500 dark:text-slate-400">
                          {t('audit.details')}
                        </summary>
                        <pre className="mt-1 max-w-xs overflow-auto rounded bg-slate-50 p-1 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                          {JSON.stringify({ before: row.beforeJson, after: row.afterJson }, null, 2)}
                        </pre>
                      </details>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
        {data?.pagination ? (
          <div className="flex items-center justify-between border-t border-slate-200 px-3 py-2 text-sm dark:border-slate-700">
            <span className="text-slate-500 dark:text-slate-400">
              {data.pagination.total} {t('common:common.rows')}
            </span>
            <div className="flex gap-1">
              <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                ‹
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={page >= data.pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                ›
              </Button>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
